import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { PageEvent } from '@angular/material/paginator';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource,
} from '@angular/material/table';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import {
  ClientsService,
  SelectedClient,
} from 'src/app/services/clients/clients.service';
import { CaseClientFilterComponent } from 'src/app/shared/payee-cases/case-client-filter/case-client-filter.component';
import { PayeeCasesService } from 'src/app/shared/payee-cases/payee-cases.service';
import { ClientOption, Pagination } from 'src/app/shared/payee-cases/types';
import { HeaderComponent } from '../header/header.component';
import { EventsTableComponent } from './events-table/events-table.component';
import { EventsClearFiltersComponent } from './filters/events-clear-filters.component';
import { EventsCaseIdFilterComponent } from './filters/events-case-id-filter.component';
import { EventsDateFilterComponent } from './filters/events-date-filter.component';
import { EventsDebtorNameFilterComponent } from './filters/events-debtor-name-filter.component';
import { EventsLabelFilterComponent } from './filters/events-label-filter.component';
import { EventsService } from './events.service';
import { EventTableItem } from './types';

@Component({
  selector: 'app-events',
  templateUrl: './events.component.html',
  styleUrls: ['./events.component.scss'],
  imports: [
    HeaderComponent,
    EventsCaseIdFilterComponent,
    EventsDebtorNameFilterComponent,
    EventsLabelFilterComponent,
    EventsDateFilterComponent,
    CaseClientFilterComponent,
    EventsClearFiltersComponent,
    EventsTableComponent,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    TranslateModule,
  ],
})
export class EventsComponent implements OnInit, OnDestroy {
  private eventsService = inject(EventsService);
  private payeeCasesService = inject(PayeeCasesService);
  private clientsService = inject(ClientsService);
  private router = inject(Router);
  private readonly destroy = new Subject<void>();

  readonly dataSource = new MatTableDataSource<EventTableItem>();
  readonly skeletonDataSource = new MatTableDataSource<unknown>(
    Array(8).fill({}),
  );
  pagination?: Pagination;

  private readonly hasMultipleClients = signal(false);
  private readonly clientFilter = toSignal(this.payeeCasesService.client);
  readonly showClientColumn = computed(
    () => this.hasMultipleClients() && !this.clientFilter(),
  );
  readonly skeletonColumns = computed(() => {
    const count = this.showClientColumn() ? 7 : 6;
    return Array.from({ length: count }, (_, i) => `skeleton-col-${i + 1}`);
  });

  readonly searchClients = async (name: string): Promise<ClientOption[]> => {
    const clients = this.clientsService.clientsSubject.value ?? [];
    const needle = name.toLowerCase();
    return clients
      .filter((c) => (c.name ?? '').toLowerCase().includes(needle))
      .map(
        (c): ClientOption => ({
          uuid: c.id,
          name: c.name ?? '',
        }),
      );
  };

  readonly resolveClient = async (
    uuid: string,
  ): Promise<ClientOption | null> => {
    const clients = this.clientsService.clientsSubject.value ?? [];
    const client = clients.find((c) => c.id === uuid);
    if (!client) {
      return null;
    }
    return {
      uuid: client.id,
      name: client.name ?? '',
    };
  };

  ngOnInit(): void {
    this.payeeCasesService.getDebtorsPath.next('api/debtor');
    this.eventsService.resetFilters();
    this.eventsService.sort_by.next('happened_at');
    this.eventsService.sort_direction.next('desc');

    this.clientsService.clientsSubject
      .pipe(takeUntil(this.destroy))
      .subscribe((clients: SelectedClient[] | null) => {
        this.hasMultipleClients.set((clients?.length ?? 0) > 1);
      });

    this.eventsService.events
      .pipe(takeUntil(this.destroy))
      .subscribe((events) => {
        this.pagination = events.pagination;
        this.dataSource.data = events.data;
      });

    this.eventsService.onInit();

    this.eventsService.filtersWithPagination
      .pipe(takeUntil(this.destroy))
      .subscribe(() => this.setFilterQueryParams());
  }

  private setFilterQueryParams(): void {
    void this.router.navigate([], {
      queryParams: this.eventsService.getFilters(),
      queryParamsHandling: 'merge',
    });
  }

  ngOnDestroy(): void {
    this.eventsService.onDestroy();
    this.destroy.next();
    this.destroy.complete();
  }

  get loading(): boolean {
    return this.eventsService.loading();
  }

  pageChange(event: PageEvent): void {
    this.eventsService.setPagination(event);
  }
}
